import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';

export const Layout = ({ children, hideFooter = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/treatments', label: 'Treatments' },
    { path: '/home-visits', label: 'Home Visits' },
    { path: '/faq', label: 'FAQ' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact Us' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="w-20 h-20 rounded-full overflow-hidden">
                <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors ${isActive(link.path) ? 'text-slate-900 underline underline-offset-4' : 'text-slate-800 hover:text-slate-900'}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <a 
                href="tel:0614481090" 
                className="hidden md:flex items-center gap-2 text-sm text-slate-800 hover:text-slate-900 transition-colors"
                data-testid="header-phone"
              >
                <Phone className="w-4 h-4" />
                <span>061 448 1090</span>
              </a>
              
              <Link
                to={user ? '/admin' : '/login'}
                className="text-sm font-medium text-slate-800 hover:text-slate-900 transition-colors"
                data-testid="login-link"
              >
                {user ? 'Dashboard' : 'Login'}
              </Link>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 text-slate-800"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#5ce1e6] border-t border-[#22d3ee]">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 px-3 rounded-lg text-sm ${
                    isActive(link.path) 
                      ? 'bg-white/20 text-slate-900' 
                      : 'text-slate-800 hover:bg-white/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <a 
                href="tel:0614481090" 
                className="flex items-center gap-2 py-2 px-3 text-sm text-slate-800"
              >
                <Phone className="w-4 h-4" />
                <span>061 448 1090</span>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {!hideFooter && <Footer />}

      {/* WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
};

export default Layout;
